import { Maybe } from 'yup/lib/types';
import { TranslationKey } from '~/lib/data-contract/auto-generated/translation-model';
import {
    ImageGroup,
    Product,
    Store,
    VariationAttributeValue,
} from '~/lib/data-contract/salesforce';
import { TNotification } from '~/shared/components/ProductCard/components/Notifications';
import { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import {
    ProductSpecs,
    SummarySpecificationProps,
} from '~/shared/components/Summary/components/SummarySpecifications';
import { TranslateFn, useTranslation } from '~/shared/utils/translation/hooks/useTranslation';
import { useSomeVariant } from './hooks/use-variant';
import { P50ProductDetailsPage, ProductCategories, SFProduct, TVariant } from './pdp.definitions';
import { ProductLabelSettings } from '~/lib';
import { findImageGroupBy } from '~/utils/image-group-utils';

/**
 * Logic for splitting media up needs
 * to be made.
 * see https://jira.impact.dk/browse/INP-1382
 * */
export const getImages = (imageGroups?: SFProduct | null) =>
    imageGroups && imageGroups[0]?.images.map((image: any) => ({ ...image, src: image.link }));

export const getImagesForVariation = (product?: Product) => {
    const imageGroup: ImageGroup = findImageGroupBy(product?.imageGroups, {
        viewType: 'large',
        selectedVariationAttributes: product?.variationValues ?? {},
    });

    return imageGroup?.images?.map((image: any) => ({ ...image, src: image.link }));
};

export const createNotifications = (
    product: Product,
    translate: TranslateFn,
    convertPriceToLocale: (price: number) => string,
    frameProductSettings?: ProductLabelSettings,
): TProductDetails['notifications'] => {
    const leftInfo: TNotification = { text: '', type: 'secondary' };
    const rightInfo: TNotification = { text: '', type: 'primary' };
    const minimumShippingCost = product?.c_minimumShippingCost || product?.cust_minimumShippingCost;

    const deliveryDays = product?.c_deliveryDaysText || product?.cust_deliveryDaysText;
    const persuasiveText = product?.c_persuasiveText || product?.cust_persuasiveText;

    if (minimumShippingCost) {
        switch (Number(minimumShippingCost)) {
            case 0: {
                leftInfo.text += translate('plp.freeShipping');
                break;
            }
            default: {
                // hide labels if they are explicitly off, otherwise show them
                if (frameProductSettings?.showFreightPrice == false) {
                    break;
                } else {
                    leftInfo.text += translate('checkout.freightCostFrom', {
                        amount: convertPriceToLocale(minimumShippingCost),
                    });
                }
            }
        }
    }

    if (deliveryDays) {
        leftInfo.text += (leftInfo.text ? ' — ' : '') + deliveryDays;
    }

    if (persuasiveText) {
        const inStock = parseInt(persuasiveText, 10);
        if (0 < inStock && inStock < 3) {
            rightInfo.text = translate('pdp.persuasive', { amount: inStock.toString() });
        }
    }
    const notifications = [leftInfo, rightInfo];

    if (product?.c_prices && product?.c_prices?.length > 1) {
        return notifications;
    }
    return notifications.filter((notis) => notis.text);
};

export const mapProductData = (
    product: Product,
    translate: TranslateFn,
    convertPriceToLocale: (price: number) => string,
): TProductDetails => {
    return {
        potentialBonusEarning: product.c_potentialBonusEarning
            ? !!parseFloat(product.c_potentialBonusEarning || 0) &&
              translate('pdp.bonusEarning', {
                  amount: convertPriceToLocale(product.c_potentialBonusEarning),
              })
            : 0,
        cust_testwinner: product.c_ItemTestWinnerDescription,
        splash: product.c_splash && { text: product.c_splash, variant: 'campaign' },
        brand: { text: product.brand },
        name: `${product.name}`,
        cust_synopsis: product.c_synopsis ?? null,
        cust_promotionType: product.c_activepromo?.type,
        cust_promotionId:
            typeof product.c_activepromo?.promotionid === 'string' &&
            (product.c_activepromo?.promotionid?.startsWith('HOOF-') ||
                product.c_activepromo?.promotionid?.startsWith('OF-')) // NOTE: OF- is used for ImercoPrice
                ? product.c_activepromo.promotionid
                : undefined,
        cust_isNew: product.c_isNew ?? false,
        cust_in_stock: product.inventory?.orderable,
        canBePersonalized: product.c_personalization,
        notifications: createNotifications(product, translate, convertPriceToLocale),
        prices: product.c_prices,
        id: product.id,
        ctaButtons: {
            primary: product?.c_buyButtonsObj?.c_primaryButton,
            secondary: product?.c_buyButtonsObj?.c_secondaryButton,
        },
    } as TProductDetails;
};

const parseSpecifications = (specifications: string, translate: TranslateFn) => {
    try {
        if (!specifications) return [];

        const parsed = JSON.parse(specifications);

        if (!parsed) return [];

        //NOTE: Units Bunddiameter, Kapacitetsenhed and Kapacitet sætUnit
        //are merged with their respective values. They should not be shown individually.

        if (parsed?.Bunddiameter && parsed?.BunddiameterUnit)
            parsed.Bunddiameter += ` ${parsed.BunddiameterUnit}`;
        delete parsed.BunddiameterUnit;

        if (parsed?.Kapacitet && parsed?.KapacitetUnit) {
            parsed.Kapacitet += ` ${parsed.KapacitetUnit}`;
        } else if (parsed?.Kapacitet && parsed?.Kapacitetsenhed) {
            parsed.Kapacitet += ` ${parsed.Kapacitetsenhed}`;
        }

        delete parsed.KapacitetUnit;
        delete parsed.Kapacitetsenhed;

        if (parsed?.['Kapacitet sæt'] && parsed?.['Kapacitet sætUnit'])
            parsed['Kapacitet sæt'] += ` ${parsed['Kapacitet sætUnit']}`;
        delete parsed['Kapacitet sætUnit'];

        if (parsed?.['Fyldvægt'] && parsed?.['FyldvægtUnit'])
            parsed['Fyldvægt'] += ` ${parsed['FyldvægtUnit']}`;
        delete parsed['FyldvægtUnit'];

        const result = Object.entries(parsed).map(([name, text]) => ({
            name,
            text: Array.isArray(text)
                ? text.join(', ')
                : typeof text === 'boolean' ||
                    text === 'True' ||
                    text === 'False' ||
                    text === 'true' ||
                    text === 'false'
                  ? translate(`pdp.spec.${String(text).toLocaleLowerCase()}` as TranslationKey)
                  : text,
        }));

        if (result) {
            return result as ProductSpecs[];
        }
        return [];
    } catch (e) {
        console.debug(e);
        return [];
    }
};

/**
 * List of spec names from the product from salesforce
 * we would like to use in the summary.
 * We need to add an Umbraco translation key for each.
 */
const specAttributes = ['c_color', 'c_material'];
const customerSpecAttributes = [
    'c_ItemTestWinnerDescription',
    'ean',
    'id',
    'unit',
    'c_ProductDesigner',
    'c_modelNumber',
];
const storePersonnelSpecAttributes = [
    'c_ItemTestWinnerDescription',
    'ean',
    'id',
    'c_productStatusCode',
    'unit',
    'c_ProductDesigner',
    'c_modelNumber',
    'c_SupplierName',
    'c_SupplierNo',
];

enum SpecificationItemProp {
    brand = 'brand', // The brand or producer of the product.
    c_color = 'color', // The color of the product.
    c_dimDepth = 'depth', // The depth or height of the product.
    c_dimDiameter = 'diameter', // The diameter of the product.
    c_dimHeight = 'height', // The height or depth of the product.
    c_dimLength = 'length', // The length of the product.
    c_dimWeight = 'weight', // The weight of the product.
    c_dimWidth = 'width', // The width of the product.
    c_material = 'material', // The material of the product.
    c_modelNumber = 'modelNumber', // The model number of the product.
    c_ProductDesigner = 'productDesigner', // The designer of the product.
    c_ItemFreeTextDimension = 'ItemFreeTextDimension',
    availability = 'availability', // The availability status of the product.
    category = 'category', // The category of the product.
    ean = 'gtin13', // The GTIN-13 code of the product.
    id = 'mpn', // The manufacturer part number of the product.
    isPartOf = 'series', // The product series or group.
    series = 'series', // The product series or group.
    subCategory = 'subcategory', // The subcategory of the product.
    unit = 'quantity', // The quantity of the product.
}

type SpecificationProps = {
    product?: P50ProductDetailsPage['data']['product'];
    categories: ProductCategories;
};

export const getSpecs = (props: SpecificationProps) => {
    return getSpecsInner(customerSpecAttributes, props);
};

export const getStorePersonnelSpecs = (props: SpecificationProps) => {
    return getSpecsInner(storePersonnelSpecAttributes, props);
};

const getSpecsInner = (
    extraSpecs: string[],
    { categories, product }: SpecificationProps,
): SummarySpecificationProps['specifications'] | null => {
    const { translate } = useTranslation();
    if (!product) return null;

    /* Links to other PLP pages, we get this from Umbraco on the page itself */
    const [brand, serie, ...res] = Object.entries(categories).map(([key, value = {}]) => ({
        ...value,
        name: translate(`pdp.spec.${key}` as TranslationKey),
        itemProp: SpecificationItemProp[key as keyof typeof SpecificationItemProp] || undefined,
    }));

    /* See what are available on the product */
    const specsAvailable = Object.keys(product ?? {}).filter((key) => specAttributes.includes(key));
    const specsAvailable2 = extraSpecs.filter((key) => product[key]);

    /* Map the product to the spec names */
    const specs = specsAvailable.map((key) => ({
        name: translate(`pdp.spec.${key}` as TranslationKey),
        text: product[key]?.replace(/,/g, ', '),
        itemProp: SpecificationItemProp[key as keyof typeof SpecificationItemProp] || undefined,
    }));

    const specs2 = specsAvailable2?.map((key) => ({
        name: translate(`pdp.spec.${key}` as TranslationKey),
        text: product[key]?.replace(/,/g, ', '),
        itemProp: SpecificationItemProp[key as keyof typeof SpecificationItemProp] || undefined,
    }));

    /* Get the rest of the specs from the product */
    const restSpecs = parseSpecifications(product.c_specifications, translate);

    if (product.c_dimWeight && product.c_dimWeightUnit) {
        restSpecs.push({
            name: translate('pdp.spec.c_dimWeight'),
            text: `${product.c_dimWeight} ${product.c_dimWeightUnit}`,
            itemProp:
                SpecificationItemProp['c_dimWeight' as keyof typeof SpecificationItemProp] ||
                undefined,
        });
    }

    if (product.c_ItemFreeTextDimension) {
        restSpecs.push({
            name: translate('pdp.spec.c_ItemFreeTextDimension'),
            text: product.c_ItemFreeTextDimension,
            itemProp: SpecificationItemProp['c_ItemFreeTextDimension'],
        });
    }

    ['c_dimHeight', 'c_dimWidth', 'c_dimLength', 'c_dimDepth', 'c_dimDiameter'].forEach((x) => {
        const result = product[x];
        if (result && product.c_dimMessurementUnit) {
            restSpecs.push({
                name: translate(`pdp.spec.${x}` as TranslationKey),
                text: `${result} ${product.c_dimMessurementUnit}`,
                itemProp: SpecificationItemProp['unit'] || undefined,
            });
        }
    });

    return [brand, serie, ...specs, ...restSpecs, ...res, ...specs2].filter((x) => x.text);
};

/**
 * Modifies a given url by adding/updating query parameters.
 *
 * @param {string} url - The base url of the output url set.
 * @param {object} extraParams - A key values pairing used to add static search param values.
 * @returns {string} A URL with additional params
 * @example
 * import {rebuildPathWithParams} from '/path/to/utils/url'
 *
 * rebuildPathWithParams(
 *     '/en-GB/product/25501032M',
 *     {color: 'JJ2SKXX', size: 'MD'}
 * )
 *
 * // Returns
 * // '/en-GB/product/25501032M?color=JJ2SKXX&size=9MD'
 */
export const rebuildPathWithParams = (url: string, extraParams: any) => {
    const [pathname, search] = url.split('?');
    const params = new URLSearchParams(search);

    // Apply any extra params.
    Object.keys(extraParams).forEach((key) => {
        const value = extraParams[key];

        // 0 is a valid value as for a param
        if (!value && value !== 0) {
            params.delete(key);
        } else {
            params.set(key, value);
        }
    });

    // Clean up any trailing `=` for params without values.
    const paramStr = params.toString().replace(/=&/g, '&').replace(/=$/, '');

    // Generate the newly updated url.
    return `${pathname}${Array.from(paramStr).length > 0 ? `?${paramStr}` : ''}`;
};

export const getAttributeOptions = (options: any[] = [], attr: string) => {
    return options.reduce((acc, curr) => {
        const a = [...acc];
        curr.values.forEach((c: any) => {
            if (curr.id === attr) {
                a.push(c);
            }
        });
        return a;
    }, []);
};

export const formatVariantOptions = (
    variant: any,
    variants: TVariant[],
    selectedAttributes: any,
    id: string,
) => {
    const mapped = variant.values.map((variant: VariationAttributeValue) => {
        const hasVariant: TVariant[] = useSomeVariant(variants, {
            supplierColour: selectedAttributes?.['supplierColour'],
            [id]: variant.value,
        });

        const orderable = hasVariant?.some((x) => x.orderable);
        return {
            ...variant,
            label: variant.name,
            value: variant.value,
            disabled: !orderable,
            className: orderable ? '' : 'disabled',
        };
    });

    return { ...variant, values: mapped };
};

export const getDisplayPrice = (product: Product, isRegistered: boolean) => {
    const memberprice = product.c_memberprice;

    if (isRegistered && memberprice) {
        return memberprice;
    }

    if (!memberprice && product.c_effectivePrice) {
        return product.c_effectivePrice;
    }

    return product.price;
};

export const getFilteredStores = (
    storeIds: string[] = [],
    stores: Store[] = [],
    limit = 20,
): Store[] => {
    const limitedStoreIds = storeIds?.slice(0, limit);
    return stores.filter((store) => limitedStoreIds?.includes(store.id));
};

export const extractMetaImage = (product: Maybe<SFProduct>): ImageGroup | undefined => {
    if (!product || !product?.imageGroups?.length) return undefined;

    const color = product.variationValues?.supplierColour;
    const size = product.variationValues?.size;

    const metaImage = product.imageGroups.find((imageGroup: ImageGroup) => {
        if (imageGroup.viewType !== 'large') return false;

        const variationColor = imageGroup.variationAttributes?.[0]?.values?.[0].value;
        const variationSize = imageGroup.variationAttributes?.[1]?.values?.[0].value;

        if (color && size) {
            return variationColor === color && variationSize === size;
        } else if (color) {
            return variationColor === color;
        } else if (size) {
            return variationSize === size;
        }

        return false;
    });

    return metaImage;
};
